import { useRef, type ReactNode } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { haptic } from '@/telegram';
import { useSheetDrag } from './useSheetDrag';
import { t } from '../i18n';

interface SheetProps {
  open: boolean;
  title: string;
  /** Короткая строка под заголовком: «План: 550.00 BYN · до 5 сентября». */
  description?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  /**
   * false — не фокусировать первое поле при открытии. Нужно листу со своей
   * цифровой клавиатурой: иначе фокус уйдёт в «Заметку» и вылезет системная.
   */
  autoFocus?: boolean;
}

/**
 * Модалка снизу на Radix Dialog: фокус-ловушка, Escape и блокировка скролла —
 * из коробки. Заголовок липкий, скроллится только содержимое: в длинных формах
 * (платёж, трата) иначе непонятно, что за лист открыт.
 */
export function Sheet({
  open,
  title,
  description,
  onClose,
  children,
  autoFocus = true,
}: SheetProps): JSX.Element {
  const contentRef = useRef<HTMLDivElement>(null);
  const drag = useSheetDrag(contentRef, () => {
    haptic.tap('soft');
    onClose();
  });
  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          haptic.tap('soft');
          onClose();
        }
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 animate-fade-in bg-[var(--overlay)] data-[state=closed]:animate-fade-out" />
        <Dialog.Content
          className="fixed inset-x-0 bottom-0 z-50 flex max-h-[calc(100dvh-var(--safe-top)-12px)] animate-sheet-in flex-col data-[state=closed]:animate-sheet-out rounded-t-[28px] bg-popover text-popover-foreground outline-none"
          ref={contentRef}
          tabIndex={-1}
          // Без автофокуса в поле фокус всё равно уходит в лист, а не остаётся
          // на кнопке под модалкой: иначе скринридер и Tab стартуют снаружи.
          onOpenAutoFocus={
            autoFocus
              ? undefined
              : (event) => {
                  event.preventDefault();
                  contentRef.current?.focus();
                }
          }
        >
          <div className="shrink-0 cursor-grab touch-none px-4 pt-2 select-none" {...drag}>
            <div className="mx-auto mb-2 h-[5px] w-9 rounded-full bg-border-strong" />
            <div className="mb-3 flex items-start justify-between gap-2">
              <div className="min-w-0 pt-1">
                <Dialog.Title className="truncate text-[20px] font-bold tracking-[-0.02em]">
                  {title}
                </Dialog.Title>
                {description ? (
                  <Dialog.Description className="mt-0.5 text-[13px] text-muted-foreground">
                    {description}
                  </Dialog.Description>
                ) : null}
              </div>
              <Dialog.Close
                className="-mr-1 flex size-9 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground active:opacity-70"
                aria-label={t('components.close')}
              >
                <X className="size-4" aria-hidden />
              </Dialog.Close>
            </div>
          </div>

          <div
            className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4"
            style={{ paddingBottom: 'calc(1rem + var(--safe-bottom))' }}
          >
            {children}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}